import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SearchableSelect from '../common/SearchableSelect';
import { locationsInAccountLineage } from './lineage';

const AccountLocationPickerDialog = ({
  open,
  t,
  token,
  accounts,
  locations,
  accountId,
  boxId,
  onCancel,
  onConfirm,
}) => {
  const [draftAccountId, setDraftAccountId] = useState(accountId || '');
  const [draftBoxId, setDraftBoxId] = useState(boxId || '');

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraftAccountId(accountId || '');
    setDraftBoxId(boxId || '');
  }, [open, accountId, boxId, token]);

  const scopedLocations = useMemo(
    () => locationsInAccountLineage(locations, accounts, draftAccountId),
    [locations, accounts, draftAccountId]
  );

  const needsLocation = !token || token.picker === 'location';
  const canInsert = needsLocation
    ? !!(draftAccountId && draftBoxId)
    : !!draftAccountId;

  const confirm = () => {
    const account =
      (accounts || []).find((item) => String(item.id) === String(draftAccountId)) ||
      null;
    const location =
      scopedLocations.find((item) => String(item.id) === String(draftBoxId)) ||
      null;
    onConfirm({
      token,
      accountId: draftAccountId,
      boxId: draftBoxId,
      account,
      location,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      className="mcc-picker-dialog"
    >
      <DialogTitle>
        {t('MessageControl.PickStoreLocation', {
          defaultValue: 'Select store and location',
        })}
      </DialogTitle>
      <DialogContent>
        <p className="mcc-helper">
          {t('MessageControl.PickStoreLocationHelp', {
            defaultValue:
              'Locations stay in the selected store’s lineage. Child accounts and boxes under that store are listed; other pharmacies are not.',
          })}
        </p>
        <div className="mcc-picker-fields">
          <SearchableSelect
            id="mcc-token-account"
            label={t('MessageControl.Store', {
              defaultValue: 'Store (Account Name)',
            })}
            items={accounts}
            value={draftAccountId || ''}
            placeholder={t('MessageControl.SearchStores', {
              defaultValue: 'Search stores...',
            })}
            variant="outlined"
            sx={{ width: '100%' }}
            menuZIndex={1600}
            onChange={(event) => {
              const nextAccountId = event.target.value;
              setDraftAccountId(nextAccountId);
              const nextLocations = locationsInAccountLineage(
                locations,
                accounts,
                nextAccountId
              );
              const stillValid = nextLocations.some(
                (item) => String(item.id) === String(draftBoxId)
              );
              setDraftBoxId(
                stillValid ? draftBoxId : (nextLocations[0] && nextLocations[0].id) || ''
              );
            }}
          />
          <SearchableSelect
            id="mcc-token-location"
            label={t('MessageControl.Location', {
              defaultValue: 'Location Name (Boxes)',
            })}
            items={scopedLocations}
            value={draftBoxId || ''}
            placeholder={t('MessageControl.SearchLocations', {
              defaultValue: 'Search locations...',
            })}
            variant="outlined"
            sx={{ width: '100%' }}
            menuZIndex={1600}
            onChange={(event) => setDraftBoxId(event.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          {t('MessageControl.Close', { defaultValue: 'Close' })}
        </Button>
        <Button onClick={confirm} variant="contained" disabled={!canInsert}>
          {t('MessageControl.InsertToken', { defaultValue: 'Insert token' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountLocationPickerDialog;



// WEBPACK FOOTER //
// ./src/components/message-control-center/AccountLocationPicker.js